import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Tooltip, Badge, Divider } from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { gmailExpiryService } from '../services/gmailExpiryService';
import { announcementService } from '../services/announcementService';
import axios from 'axios';
import LogoutIcon from '@mui/icons-material/Logout';
import LinkIcon from '@mui/icons-material/Link';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from '@mui/icons-material/History';
import PeopleIcon from '@mui/icons-material/People';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SecurityIcon from '@mui/icons-material/Security';
import CampaignIcon from '@mui/icons-material/Campaign';
import EmailIcon from '@mui/icons-material/Email';
import DashboardIcon from '@mui/icons-material/Dashboard';

const Navigation = ({ isNavExpanded, setIsNavExpanded }) => {
  const { logout, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [expiringTokenCount, setExpiringTokenCount] = useState(0);
  const [announcementUnreadCount, setAnnouncementUnreadCount] = useState(0);
  const [masterRole, setMasterRole] = useState(null);
  const [masterRoleLoaded, setMasterRoleLoaded] = useState(false);
  const [teamName, setTeamName] = useState('');
  const location = useLocation();
  
  // Determine if sidebar should be shown
  // For bidders: wait for both auth loading and master role loading
  // For others: wait only for auth loading
  const isUserInfoLoaded = !authLoading && (user?.role !== 'bidder' || masterRoleLoaded);
  
  // Check Gmail expiry status on component mount and every hour
  useEffect(() => {
    const checkExpiryStatus = async () => {
      if (user && user.role !== 'bidder') {
        const count = await gmailExpiryService.getExpiringTokenCount();
        setExpiringTokenCount(count);
      }
    };

    // Check immediately on mount
    checkExpiryStatus();

    // Set up interval to check every hour (3600000 ms)
    const interval = setInterval(checkExpiryStatus, 3600000);

    // Listen for Gmail refresh events
    const handleGmailRefresh = () => {
      checkExpiryStatus();
    };
    window.addEventListener('gmail-status-refreshed', handleGmailRefresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener('gmail-status-refreshed', handleGmailRefresh);
    };
  }, [user]);

  // Check unread announcement count every 60 seconds
  useEffect(() => {
    const fetchAnnouncementUnreadCount = async () => {
      if (user) {
        const count = await announcementService.getUnreadCount();
        setAnnouncementUnreadCount(count);
      }
    };

    fetchAnnouncementUnreadCount();
    const interval = setInterval(fetchAnnouncementUnreadCount, 60000);

    const handleAnnouncementsUpdate = () => {
      fetchAnnouncementUnreadCount();
    };
    window.addEventListener('announcements-read', handleAnnouncementsUpdate);
    window.addEventListener('announcements-new', handleAnnouncementsUpdate);

    return () => {
      clearInterval(interval);
      window.removeEventListener('announcements-read', handleAnnouncementsUpdate);
      window.removeEventListener('announcements-new', handleAnnouncementsUpdate);
    };
  }, [user]);

  useEffect(() => {
    const fetchTeamInfo = async () => {
      if (!user?.team) {
        setTeamName('');
        return;
      }
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/teams/my-team-info`);
        setTeamName(response.data?.team?.name || '');
      } catch (error) {
        console.error('Error fetching team info:', error);
        setTeamName('');
      }
    };

    if (user) {
      fetchTeamInfo();
    } else {
      setTeamName('');
    }
  }, [user, user?.team]);

  // Check master role for bidders
  useEffect(() => {
    const fetchMasterRole = async () => {
      try {
        if (user && user.role === 'bidder' && user.master) {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/teams/my-team`);
          const master = response.data.find(member => 
            member._id && member._id.toString() === user.master.toString()
          );
          if (master) {
            setMasterRole(master.role);
          } else {
            // Master not found, set to null to indicate restricted access
            setMasterRole(null);
          }
        } else if (user && user.role === 'bidder' && !user.master) {
          // Bidder without master should not see bid links
          setMasterRole(null);
        }
        setMasterRoleLoaded(true);
      } catch (error) {
        console.error('Error fetching master role:', error);
        // On error, deny access to be safe
        setMasterRole(null);
        setMasterRoleLoaded(true);
      }
    };

    if (user && user.role === 'bidder') {
      setMasterRoleLoaded(false);
      fetchMasterRole();
    } else {
      setMasterRoleLoaded(true);
    }
  }, [user, user?.role, user?.master]);

  const announcementBadgeSx = {
    '& .MuiBadge-badge': {
      fontSize: '0.75rem',
      minWidth: '18px',
      height: '18px',
      borderRadius: '9px',
      fontWeight: 600,
    }
  };

  const renderAnnouncementsItem = () => {
    const tooltipTitle = announcementUnreadCount > 0
      ? `Announcements (${announcementUnreadCount} unread)`
      : 'Announcements';

    return (
      <Tooltip title={tooltipTitle} placement="right" arrow disableHoverListener={isNavExpanded}>
        <ListItemButton
          component={RouterLink}
          to="/announcements"
          sx={{
            "&.Mui-selected": {
              backgroundColor: "action.selected",
            },
            justifyContent: isNavExpanded ? 'initial' : 'center',
            px: 2.5,
            overflow: 'visible',
          }}
          selected={location.pathname === "/announcements"}
        >
          <ListItemIcon sx={{
            minWidth: isNavExpanded ? 56 : 'auto',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'visible',
          }}>
            <Badge
              badgeContent={announcementUnreadCount}
              color="error"
              overlap="circular"
              invisible={announcementUnreadCount === 0}
              sx={announcementBadgeSx}
            >
              <CampaignIcon sx={{ color: "primary.main" }} />
            </Badge>
          </ListItemIcon>
          {isNavExpanded && <ListItemText primary="Announcements" />}
        </ListItemButton>
      </Tooltip>
    );
  };

  const renderSectionLabel = (label) => {
    if (!isNavExpanded) {
      return <Divider sx={{ my: 1, mx: 1.5 }} />;
    }
    return (
      <Typography
        variant="overline"
        sx={{
          display: 'block',
          px: 2.5,
          pt: 1.5,
          pb: 0.5,
          color: 'text.secondary',
          fontSize: '0.65rem',
          letterSpacing: '0.08em',
          lineHeight: 1.6,
        }}
      >
        {label}
      </Typography>
    );
  };

  const renderGeneralNavItems = () => (
    <>
        {masterRoleLoaded && user?.role !== 'restricted developer' && !(user?.role === 'bidder' && (masterRole === 'restricted developer' || !user.master || (user.master && !masterRole))) && (
          <Tooltip title="Bid Links" placement="right" arrow disableHoverListener={isNavExpanded}>
            <ListItemButton
              component={RouterLink}
              to="/"
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "action.selected",
                },
                justifyContent: isNavExpanded ? 'initial' : 'center',
                px: 2.5,
              }}
              selected={location.pathname === "/"}
            >
              <ListItemIcon sx={{ 
                minWidth: isNavExpanded ? 56 : 'auto',
                justifyContent: 'center',
              }}>
                <LinkIcon sx={{ color: "primary.main" }} />
              </ListItemIcon>
              {isNavExpanded && <ListItemText primary="Bid Links" />}
            </ListItemButton>
          </Tooltip>
        )}

        {masterRoleLoaded && user?.role !== 'restricted developer' && !(user?.role === 'bidder' && (masterRole === 'restricted developer' || !user.master || (user.master && !masterRole))) && (
          <Tooltip title="Dashboard" placement="right" arrow disableHoverListener={isNavExpanded}>
            <ListItemButton
              component={RouterLink}
              to="/dashboard"
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "action.selected",
                },
                justifyContent: isNavExpanded ? 'initial' : 'center',
                px: 2.5,
              }}
              selected={location.pathname === "/dashboard"}
            >
              <ListItemIcon sx={{
                minWidth: isNavExpanded ? 56 : 'auto',
                justifyContent: 'center',
              }}>
                <DashboardIcon sx={{ color: "primary.main" }} />
              </ListItemIcon>
              {isNavExpanded && <ListItemText primary="Dashboard" />}
            </ListItemButton>
          </Tooltip>
        )}

        {user?.role !== 'bidder' && (
          <Tooltip title="Resumes" placement="right" arrow disableHoverListener={isNavExpanded}>
            <ListItemButton
              component={RouterLink}
              to="/resumes"
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "action.selected",
                },
                justifyContent: isNavExpanded ? 'initial' : 'center',
                px: 2.5,
              }}
              selected={location.pathname === "/resumes"}
            >
              <ListItemIcon sx={{ 
                minWidth: isNavExpanded ? 56 : 'auto',
                justifyContent: 'center',
                position: 'relative',
              }}>
                <DescriptionIcon sx={{ color: "primary.main" }} />
              </ListItemIcon>
              {isNavExpanded && (
                <ListItemText 
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <span>Resumes</span>
                      {expiringTokenCount > 0 && (
                        <Badge 
                          badgeContent={expiringTokenCount} 
                          color="error"
                          sx={{
                            '& .MuiBadge-badge': {
                              fontSize: '0.75rem',
                              minWidth: '18px',
                              height: '18px',
                              borderRadius: '9px',
                            }
                          }}
                        />
                      )}
                    </Box>
                  } 
                />
              )}
            </ListItemButton>
          </Tooltip>
        )}

        {user?.role !== 'bidder' && (
          <Tooltip title="Manual Email" placement="right" arrow disableHoverListener={isNavExpanded}>
            <ListItemButton
              component={RouterLink}
              to="/manual-email-applications"
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "action.selected",
                },
                justifyContent: isNavExpanded ? 'initial' : 'center',
                px: 2.5,
              }}
              selected={location.pathname === "/manual-email-applications"}
            >
              <ListItemIcon sx={{ 
                minWidth: isNavExpanded ? 56 : 'auto',
                justifyContent: 'center',
              }}>
                <EmailIcon sx={{ color: "primary.main" }} />
              </ListItemIcon>
              {isNavExpanded && <ListItemText primary="Manual Email" />}
            </ListItemButton>
          </Tooltip>
        )}

        {(user?.role === 'lead' || user?.role === 'boss') && (
          <Tooltip title="Job Search" placement="right" arrow disableHoverListener={isNavExpanded}>
            <ListItemButton
              component={RouterLink}
              to="/search-queries"
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "action.selected",
                },
                justifyContent: isNavExpanded ? 'initial' : 'center',
                px: 2.5,
              }}
              selected={location.pathname === "/search-queries"}
            >
              <ListItemIcon sx={{ 
                minWidth: isNavExpanded ? 56 : 'auto',
                justifyContent: 'center',
              }}>
                <SearchIcon sx={{ color: "primary.main" }} />
              </ListItemIcon>
              {isNavExpanded && <ListItemText primary="Job Search" />}
            </ListItemButton>
          </Tooltip>
        )}

        {/* History - Show to all users including bidders */}
        <Tooltip title="History" placement="right" arrow disableHoverListener={isNavExpanded}>
          <ListItemButton
            component={RouterLink}
            to="/bid-history"
            sx={{
              "&.Mui-selected": {
                backgroundColor: "action.selected",
              },
              justifyContent: isNavExpanded ? 'initial' : 'center',
              px: 2.5,
            }}
            selected={location.pathname === "/bid-history"}
          >
            <ListItemIcon sx={{ 
              minWidth: isNavExpanded ? 56 : 'auto',
              justifyContent: 'center',
            }}>
              <HistoryIcon sx={{ color: "primary.main" }} />
            </ListItemIcon>
            {isNavExpanded && <ListItemText primary="History" />}
          </ListItemButton>
        </Tooltip>

        {user?.role !== 'bidder' && (
          <Tooltip title="AI Prompts" placement="right" arrow disableHoverListener={isNavExpanded}>
            <ListItemButton
              component={RouterLink}
              to="/ai-prompts"
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "action.selected",
                },
                justifyContent: isNavExpanded ? 'initial' : 'center',
                px: 2.5,
              }}
              selected={location.pathname === "/ai-prompts"}
            >
              <ListItemIcon sx={{ 
                minWidth: isNavExpanded ? 56 : 'auto',
                justifyContent: 'center',
              }}>
                <SmartToyIcon sx={{ color: "primary.main" }} />
              </ListItemIcon>
              {isNavExpanded && <ListItemText primary="AI Prompts" />}
            </ListItemButton>
          </Tooltip>
        )}

        {renderAnnouncementsItem()}
    </>
  );

  const renderManagementNavItems = () => (
    <>
      <Tooltip title="Team" placement="right" arrow disableHoverListener={isNavExpanded}>
        <ListItemButton
          component={RouterLink}
          to="/team-management"
          sx={{
            "&.Mui-selected": {
              backgroundColor: "action.selected",
            },
            justifyContent: isNavExpanded ? 'initial' : 'center',
            px: 2.5,
          }}
          selected={location.pathname === "/team-management"}
        >
          <ListItemIcon sx={{
            minWidth: isNavExpanded ? 56 : 'auto',
            justifyContent: 'center',
          }}>
            <PeopleIcon sx={{ color: "primary.main" }} />
          </ListItemIcon>
          {isNavExpanded && <ListItemText primary="Team" />}
        </ListItemButton>
      </Tooltip>
      <Tooltip title="API Key" placement="right" arrow disableHoverListener={isNavExpanded}>
        <ListItemButton
          component={RouterLink}
          to="/api-key-management"
          sx={{
            "&.Mui-selected": {
              backgroundColor: "action.selected",
            },
            justifyContent: isNavExpanded ? 'initial' : 'center',
            px: 2.5,
          }}
          selected={location.pathname === "/api-key-management"}
        >
          <ListItemIcon sx={{
            minWidth: isNavExpanded ? 56 : 'auto',
            justifyContent: 'center',
          }}>
            <SecurityIcon sx={{ color: "primary.main" }} />
          </ListItemIcon>
          {isNavExpanded && <ListItemText primary="API Key" />}
        </ListItemButton>
      </Tooltip>
    </>
  );

  const renderNavigationItems = () => {
    if (user?.role === 'boss') {
      return (
        <>
          {renderSectionLabel('General')}
          {renderGeneralNavItems()}
          {renderSectionLabel('Management')}
          {renderManagementNavItems()}
        </>
      );
    }

    return renderGeneralNavItems();
  };

  // Don't render sidebar until user info is fully loaded
  if (!isUserInfoLoaded) {
    return null;
  }

  return (
    <>
      <Box
        component="nav"
        sx={{
          width: isNavExpanded ? 220 : 65,
          flexShrink: 0,
          height: "100vh",
          bgcolor: "background.paper",
          position: "fixed",
          left: 0,
          top: 0,
          borderRight: (theme) => `1px solid ${theme.palette.divider}`,
          transition: 'width 0.3s ease',
        }}
      >
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}>
          {isNavExpanded && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src="logo.png" alt="Bid AI Logo" style={{ height: '24px', width: 'auto' }} />
              <Typography variant="h6" sx={{ color: "primary.main" }}>
                Bid AI
              </Typography>
            </Box>
          )}
          <IconButton onClick={() => setIsNavExpanded(!isNavExpanded)}>
            {isNavExpanded ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
        </Box>

        {user && (
          isNavExpanded ? (
            <Box
              sx={{
                px: 2,
                pb: 1.5,
                mx: 1,
                mb: 0.5,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              {teamName && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  title={teamName}
                  sx={{ display: 'block', lineHeight: 1.4 }}
                >
                  {teamName}
                </Typography>
              )}
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                title={user.name}
                sx={{ lineHeight: 1.4 }}
              >
                {user.name}
              </Typography>
            </Box>
          ) : (
            <Tooltip
              title={[teamName, user.name].filter(Boolean).join(' · ')}
              placement="right"
              arrow
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  pb: 1,
                  mb: 0.5,
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                <PeopleIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </Box>
            </Tooltip>
          )
        )}

        <List sx={{ overflow: 'visible' }}>
          {renderNavigationItems()}
          <Tooltip title="Settings" placement="right" arrow disableHoverListener={isNavExpanded}>
            <ListItemButton
              component={RouterLink}
              to="/settings"
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "action.selected",
                },
                justifyContent: isNavExpanded ? 'initial' : 'center',
                px: 2.5,
              }}
              selected={location.pathname === "/settings"}
            >
              <ListItemIcon sx={{ 
                minWidth: isNavExpanded ? 56 : 'auto',
                justifyContent: 'center',
              }}>
                <SettingsIcon sx={{ color: "primary.main" }} />
              </ListItemIcon>
              {isNavExpanded && <ListItemText primary="Settings" />}
            </ListItemButton>
          </Tooltip>
          <Tooltip title="Logout" placement="right" arrow disableHoverListener={isNavExpanded}>
            <ListItemButton
              onClick={() => setOpenDialog(true)}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "action.selected",
                },
                justifyContent: isNavExpanded ? 'initial' : 'center',
                px: 2.5,
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: isNavExpanded ? 56 : 'auto',
                justifyContent: 'center',
              }}>
                <LogoutIcon sx={{ color: "error.main" }} />
              </ListItemIcon>
              {isNavExpanded && <ListItemText primary="Logout" />}
            </ListItemButton>
          </Tooltip>
        </List>
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>Are you sure you want to logout?</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={() => {
              logout();
              navigate("/login");
              setOpenDialog(false);
            }}
            color="error"
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navigation;